import { API_BASE_URL } from '../config/constants';
import { auth } from '../config/firebase';

export interface ContentGenerationRequest {
  brandId: string;
  personaId?: string;
  platforms: string[];
  topic: string;
  shortDescription: string;
  tone: string;
  contentLength: 'short' | 'medium' | 'long';
  language: string;
  keywords: string[];
  sourceUrl?: string;
  format?: 'regular' | 'reel';
  generateImage?: boolean;
  imageStyle?: string;
  style_type?: string;
  aspect_ratio?: string;
  imageSize?: string;
  goal?: string;
}

export interface ContentGenerationResponse {
  content?: {
    feedback?: string;
    platforms?: Record<string, PlatformContent>;
  };
  imageUrl?: string | null;
  timestamp?: string;
  metadata?: {
    persona?: any;
    brand?: any;
    sourceAnalysis?: any;
  };
  success?: boolean;
  message?: string;
  data?: {
    contents: {
      platform: string;
      content: string | PlatformContent;
      hashtags?: string[];
    }[];
  };
  // New API response format fields
  generatedContents?: Array<{
    platform: string;
    generatedContent: string;
    contentScore?: number;
    imageUrl?: string;
    [key: string]: any;
  }>;
  brandId?: string;
  personaId?: string;
}

export interface PlatformContent {
  text?: string;
  image?: string;
  [key: string]: any;
}

interface ContentGenerationErrorInfo {
  code?: string;
  status?: number;
  errorType?: string;
  details?: string;
}

export class ContentGenerationError extends Error {
  readonly code?: string;
  readonly status?: number;
  readonly errorType?: string;
  readonly details?: string;

  constructor(message: string, info: ContentGenerationErrorInfo = {}) {
    super(message);
    this.name = 'ContentGenerationError';
    this.code = info.code;
    this.status = info.status;
    this.errorType = info.errorType;
    this.details = info.details;
  }
}

const readableErrorText = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !value.trim() || /<(?:!doctype|html|body)\b/i.test(value)) return undefined;
  return value.trim();
};

const contentGenerationResponseError = (body: unknown, status: number): ContentGenerationError => {
  const data = body && typeof body === 'object' ? body as Record<string, unknown> : {};
  const nested = data.error && typeof data.error === 'object' ? data.error as Record<string, unknown> : {};
  const code = readableErrorText(data.code) || readableErrorText(nested.code);
  const errorType = readableErrorText(data.errorType) || readableErrorText(nested.errorType);
  const details = readableErrorText(data.details) || readableErrorText(nested.details);
  const apiMessage = readableErrorText(data.message) || readableErrorText(data.error) || readableErrorText(nested.message);
  const message = code === 'image_generation_failed'
    ? 'The image could not be generated. Please try again later, or turn off image generation to generate text only.'
    : apiMessage || `Unable to generate content (HTTP ${status}). Please try again later.`;
  return new ContentGenerationError(message, { code, status, errorType, details });
};

// Define the main endpoint and fallbacks
const API_ENDPOINT = '/ai-coach/content/generate';
const FALLBACK_ENDPOINTS = [
  '/ai/content/generate', 
  '/api/content/generate'
];

// Create a sample response for debugging and fallback
const createDummyResponse = (request: ContentGenerationRequest): ContentGenerationResponse => {
  return {
    success: true,
    data: {
      contents: request.platforms.map(platform => ({
        platform,
        content: `Sample content for ${platform}. This is a fallback response for debugging.`,
        hashtags: ['#sample', '#debug']
      }))
    },
    timestamp: new Date().toISOString()
  };
};

/**
 * Generate content using the AI Coach API
 * @param data Content generation request data
 * @returns Promise with the generated content
 */
export const generateContent = async (data: ContentGenerationRequest): Promise<ContentGenerationResponse> => {
  console.log('Starting content generation with params:', data);
  
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      console.error('Authentication token not available');
      throw new ContentGenerationError('Please sign in again before generating content.', {
        code: 'authentication_required', errorType: 'authentication_error',
      });
    }

    console.log(`Attempting to generate content using endpoint: ${API_BASE_URL}${API_ENDPOINT}`);
    
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINT}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    console.log(`Response status from ${API_ENDPOINT}:`, response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API error from ${API_ENDPOINT}:`, response.status, errorText);
      let errorBody: unknown = null;
      try {
        errorBody = JSON.parse(errorText);
      } catch { /* Proxy HTML is not a usable application error. */ }

      throw contentGenerationResponseError(errorBody, response.status);
    }

    const rawText = await response.text();
    console.log('Raw API response text length:', rawText.length);
    console.log('Contains escaped newlines:', rawText.includes('\\n'));

    // Attempt to parse JSON
    let responseData: unknown;
    try {
       responseData = JSON.parse(rawText);
    } catch (parseError) {
        console.error('Failed to parse API response JSON:', parseError);
        console.error('Raw response text was:', rawText); // Log the raw text for debugging
        throw new ContentGenerationError('The server returned an invalid response. Please try again later.', {
          code: 'invalid_response', status: response.status, errorType: 'parse_error',
        });
    }

    if (!responseData || typeof responseData !== 'object' || Array.isArray(responseData)) {
      throw new ContentGenerationError('The server returned an invalid response. Please try again later.', {
        code: 'invalid_response', status: response.status, errorType: 'parse_error',
      });
    }
    const result = responseData as ContentGenerationResponse & { error?: unknown };
    // The backend's 206 fallback contains usable posts and an error warning.
    // Keep that partial-result contract, while rejecting an explicit failure.
    const hasPartialContent = response.status === 206 && Array.isArray(result.generatedContents)
      && result.generatedContents.some(item => typeof item?.generatedContent === 'string' && item.generatedContent.trim());
    if (result.success === false || (result.error && !hasPartialContent)) {
      throw contentGenerationResponseError(result, response.status);
    }

    console.log('Content generation API response:', responseData);
    
    return result;

  } catch (error) {
    console.error('Error during content generation request:', error);
    // A resolved error envelope would invoke the form's onSuccess and replace
    // existing content with an empty result. Preserve the mutation's error path.
    if (!(error instanceof ContentGenerationError) && error instanceof Error && /failed to fetch|fetch failed|network ?error|network request failed|load failed/i.test(error.message)) {
      throw new ContentGenerationError('The server response could not be received. Check your connection before trying again.', {
        code: 'network_error', errorType: 'network_error',
      });
    }
    throw error;
  }
};

// Batch content generation types
export interface BatchContentGenerationRequest {
  brandId: string;
  personaId: string;
  defaults: {
    language: string;
    tone: string;
    contentLength: 'short' | 'medium' | 'long';
    generateImage: boolean;
    aspect_ratio: string;
    style_type: string;
  };
  ideas: Array<{
    id: string;
    title: string;
    shortDescription: string;
    platforms: string[];
    keywords?: string[];
  }>;
}

export interface BatchContentGenerationResponse {
  success: boolean;
  message?: string;
  brandId?: string;
  personaId?: string;
  timestamp?: string;
  metadata?: {
    batchSize: number;
    failedGenerations: number;
    processingTimeMs: number;
    requestId: string;
    successfulGenerations: number;
    totalIdeas: number;
  };
  results?: Array<{
    id: string;
    ideaId: string;
    content: string;
    platforms: string[];
    media?: {
      url: string;
      type: string;
    };
  }>;
  // Keep backward compatibility
  data?: {
    contents: Array<{
      id: string;
      ideaId: string;
      content: string;
      platforms: string[];
      media?: {
        url: string;
        type: string;
      };
    }>;
  };
}

/**
 * Generate multiple content pieces using the AI Coach Batch API
 * @param data Batch content generation request data
 * @returns Promise with the generated contents
 */
export const generateBatchContent = async (data: BatchContentGenerationRequest): Promise<BatchContentGenerationResponse> => {
  console.log('🔄 Starting batch content generation with params:', JSON.stringify(data, null, 2));
  
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      console.error('❌ Authentication token not available');
      throw new Error('No authentication token available');
    }

    console.log('🔐 Token obtained successfully, length:', token.length);

    const batchEndpoint = '/ai-coach/content/generate/batch';
    const fullUrl = `${API_BASE_URL}${batchEndpoint}`;
    console.log(`🌐 Attempting to generate batch content using endpoint: ${fullUrl}`);
    
    const requestBody = JSON.stringify(data);
    console.log('📦 Request body:', requestBody);
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: requestBody,
    });

    console.log(`📡 Response status from ${batchEndpoint}:`, response.status);
    console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API error from ${batchEndpoint}:`, response.status, errorText);
      
      let errorDetails = errorText;
      try {
        const parsedError = JSON.parse(errorText);
        errorDetails = parsedError?.details || parsedError?.error || errorText;
      } catch (e) { /* Ignore parsing error, use raw text */ }
      
      throw new Error(`Failed to generate batch content: ${response.status} ${response.statusText}. Details: ${errorDetails}`);
    }

    const rawText = await response.text();
    console.log('Raw batch API response text length:', rawText.length);

    // Attempt to parse JSON
    let responseData: BatchContentGenerationResponse;
    try {
       responseData = JSON.parse(rawText);
    } catch (parseError) {
        console.error('Failed to parse batch API response JSON:', parseError);
        console.error('Raw response text was:', rawText);
        throw new Error('Failed to parse AI batch response. The format received from the server was invalid.');
    }

    console.log('Batch content generation API response:', responseData);
    return responseData; 

  } catch (error) {
    console.error('Error during batch content generation request:', error);
    // Return a consistent error format
    return {
      success: false,
      message: error instanceof Error ? error.message : 'An unknown error occurred during the batch request',
      data: { contents: [] },
    };
  }
};
